/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Voice status indicator component
 * @version 1.0.0
 * @since v0.0.07
 *
 * Purpose: Show current mode, connection state, who is speaking with
 * visual feedback (pulsing mic when active, speaker icons).
 *
 * Per FRONTEND_TODO.MD: Visual feedback for voice session state
 *
 * Dependencies:
 * - websocketService for voice activity events
 * - audioSettings for configuration
 */

import React, { useState, useEffect } from 'react';
import { webSocketService } from '../../services/websocket/websocketService';
import {
  VOICE_CONNECTION_STATE,
  type VoiceConnectionState
} from '../../config/audioSettings';
import { type VoiceMode, VOICE_MODES } from './VoiceControls';
import { voiceStatus, formatMessage } from '../../i18n/voiceMessages';

/**
 * Speaking participant info
 */
interface SpeakingParticipant {
  participantId: string;
  characterId?: string;
  characterName?: string;
  name: string;
  isSpeaking: boolean;
  energyLevel: number;
  lastActive: number;
}

/**
 * Resolve the best display name for a participant.
 * Priority: characterName > participantName > truncated participantId.
 */
function resolveDisplayName(
  participantId: string,
  participantName?: string,
  characterName?: string,
  characterId?: string,
  nameMap?: Map<string, string>
): { displayName: string; tooltip: string } {
  // 1. Check external name map (from props)
  const mappedName = nameMap?.get(participantId);

  // 2. Pick the best available name
  const bestName =
    characterName || mappedName || participantName || `User ${participantId.slice(0, 8)}`;

  // 3. Build hover tooltip with disambiguation
  const suffix = characterId ? characterId.slice(-4) : participantId.slice(-4);
  const tooltip = `${bestName} (#${suffix})`;

  return { displayName: bestName, tooltip };
}

export interface VoiceStatusProps {
  /** Current connection state */
  connectionState: VoiceConnectionState;
  /** Current voice mode */
  voiceMode: VoiceMode;
  /** Whether local mic is muted */
  isMuted?: boolean;
  /** Session ID for display */
  sessionId?: string;
  /** Show participant list (default: true) */
  showParticipants?: boolean;
  /** Maximum participants to display (default: 6) */
  maxParticipants?: number;
  /** Optional CSS class name */
  className?: string;
  /** Compact mode (minimal display) */
  compact?: boolean;
  /**
   * Optional map of participantId -> character name for display.
   * When provided, character names are shown instead of user IDs.
   */
  characterNameMap?: Map<string, string>;
}

/**
 * VoiceStatus Component
 *
 * Displays voice session status including:
 * - Connection state indicator (connected, connecting, error)
 * - Current listening mode (transcribe_all, wake_word, off)
 * - Active speakers with visual feedback
 * - Mute state indicator
 *
 * Subscribes to voice_activity WebSocket events for real-time updates.
 */
export const VoiceStatus: React.FC<VoiceStatusProps> = ({
  connectionState,
  voiceMode,
  isMuted = false,
  sessionId,
  showParticipants = true,
  maxParticipants = 6,
  className = '',
  compact = false,
  characterNameMap
}) => {
  const [participants, setParticipants] = useState<Map<string, SpeakingParticipant>>(new Map());
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [localEnergy, setLocalEnergy] = useState(0);

  /**
   * Subscribe to voice activity events
   */
  useEffect(() => {
    const handleVoiceActivity = (data: {
      sessionId: string;
      participantId: string;
      isSpeaking: boolean;
      energyLevel: number;
      participantName?: string;
      characterName?: string;
      characterId?: string;
      character_id?: string;
      character_name?: string;
    }) => {
      setParticipants((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(data.participantId);

        if (data.isSpeaking || existing) {
          // Resolve character name and ID from event data (supports both camelCase and snake_case)
          const charName = data.characterName || data.character_name || existing?.characterName;
          const charId = data.characterId || data.character_id || existing?.characterId;

          const { displayName } = resolveDisplayName(
            data.participantId,
            data.participantName || existing?.name,
            charName,
            charId,
            characterNameMap
          );

          updated.set(data.participantId, {
            participantId: data.participantId,
            characterId: charId,
            characterName: charName,
            name: displayName,
            isSpeaking: data.isSpeaking,
            energyLevel: data.energyLevel,
            lastActive: Date.now()
          });
        }

        return updated;
      });
    };

    const handleVoiceFeedback = (data: {
      state: string;
      participantId: string;
    }) => {
      // Update local speaking state based on feedback
      if (data.state === 'listening') {
        setLocalSpeaking(true);
      } else if (data.state === 'ready' || data.state === 'done') {
        setLocalSpeaking(false);
      }
    };

    webSocketService.subscribe('voice_activity', handleVoiceActivity);
    webSocketService.subscribe('voice_feedback', handleVoiceFeedback);

    // Cleanup inactive participants every 5 seconds
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setParticipants((prev) => {
        const updated = new Map(prev);
        for (const [id, participant] of updated) {
          if (!participant.isSpeaking && now - participant.lastActive > 10000) {
            updated.delete(id);
          }
        }
        return updated;
      });
    }, 5000);

    return () => {
      webSocketService.unsubscribe('voice_activity', handleVoiceActivity);
      webSocketService.unsubscribe('voice_feedback', handleVoiceFeedback);
      clearInterval(cleanupInterval);
    };
  }, []);

  /**
   * Update local energy level from AudioProcessor
   * This would typically be wired up via a callback from AudioProcessor
   */
  const updateLocalEnergy = (energy: number) => {
    setLocalEnergy(energy);
    setLocalSpeaking(energy > 0.02); // SPEECH_THRESHOLD
  };

  // Expose for parent component to call
  (VoiceStatus as any).updateLocalEnergy = updateLocalEnergy;

  /**
   * Get connection status display
   */
  const getConnectionDisplay = (): { icon: string; text: string; color: string; pulse: boolean; tooltip: string } => {
    switch (connectionState) {
      case VOICE_CONNECTION_STATE.CONNECTED:
        return { icon: '🟢', text: voiceStatus.connection.connected.text, color: '#22c55e', pulse: false, tooltip: voiceStatus.connection.connected.tooltip };
      case VOICE_CONNECTION_STATE.CONNECTING:
        return { icon: '🟡', text: voiceStatus.connection.connecting.text, color: '#eab308', pulse: true, tooltip: voiceStatus.connection.connecting.tooltip };
      case VOICE_CONNECTION_STATE.RECONNECTING:
        return { icon: '🟠', text: voiceStatus.connection.reconnecting.text, color: '#f97316', pulse: true, tooltip: voiceStatus.connection.reconnecting.tooltip };
      case VOICE_CONNECTION_STATE.ERROR:
        return { icon: '🔴', text: voiceStatus.connection.error.text, color: '#ef4444', pulse: false, tooltip: voiceStatus.connection.error.tooltip };
      default:
        return { icon: '⚪', text: voiceStatus.connection.disconnected.text, color: '#6b7280', pulse: false, tooltip: voiceStatus.connection.disconnected.tooltip };
    }
  };

  /**
   * Get mode display
   */
  const getModeDisplay = () => {
    const mode = VOICE_MODES[voiceMode];
    return {
      icon: mode.icon,
      label: mode.label,
      description: mode.description
    };
  };

  const connStatus = getConnectionDisplay();
  const modeDisplay = getModeDisplay();
  const activeSpeakers = Array.from(participants.values())
    .filter((p) => p.isSpeaking)
    .slice(0, maxParticipants);

  // Compact mode: Just icons
  if (compact) {
    return (
      <div className={`voice-status-compact ${className}`} style={styles.compactContainer} role="region" aria-label={voiceStatus.region.ariaLabel}>
        <span
          style={{
            ...styles.compactIcon,
            animation: connStatus.pulse ? 'pulse 1.5s ease-in-out infinite' : 'none'
          }}
          title={connStatus.text}
          aria-label={connStatus.tooltip}
          role="status"
        >
          {connStatus.icon}
        </span>
        <span style={styles.compactIcon} title={modeDisplay.label} aria-label={`${voiceStatus.mode.ariaLabelPrefix}${modeDisplay.label}`}>
          {modeDisplay.icon}
        </span>
        {isMuted && (
          <span style={{ ...styles.compactIcon, color: '#ef4444' }} title={voiceStatus.mute.label} aria-label={voiceStatus.mute.indicator}>
            🔇
          </span>
        )}
        {localSpeaking && !isMuted && (
          <span
            style={{ ...styles.compactIcon, animation: 'pulse 0.5s ease-in-out infinite' }}
            title={voiceStatus.local.speaking}
            aria-label={voiceStatus.local.speaking}
          >
            🎤
          </span>
        )}
        {activeSpeakers.length > 0 && (
          <span style={styles.compactBadge} title={formatMessage(voiceStatus.participants.ariaLabel, { count: activeSpeakers.length, plural: activeSpeakers.length === 1 ? '' : 's' })} aria-label={formatMessage(voiceStatus.participants.ariaLabel, { count: activeSpeakers.length, plural: activeSpeakers.length === 1 ? '' : 's' })}>
            {activeSpeakers.length}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`voice-status ${className}`} style={styles.container} role="region" aria-label={voiceStatus.region.ariaLabel}>
      {/* Header: Connection + Mode */}
      <div style={styles.header}>
        {/* Connection Status */}
        <div style={styles.statusItem} role="status" aria-live="polite" aria-atomic="true">
          <span
            style={{
              ...styles.statusIcon,
              animation: connStatus.pulse ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }}
            aria-label={connStatus.tooltip}
            title={connStatus.tooltip}
          >
            {connStatus.icon}
          </span>
          <span style={{ ...styles.statusText, color: connStatus.color }} title={connStatus.tooltip}>
            {connStatus.text}
          </span>
        </div>

        {/* Mode Display */}
        <div style={styles.statusItem} title={modeDisplay.description} aria-label={`${voiceStatus.mode.ariaLabelPrefix}${modeDisplay.label}`}>
          <span style={styles.statusIcon} aria-hidden="true">{modeDisplay.icon}</span>
          <span style={styles.statusText}>{modeDisplay.label}</span>
        </div>

        {/* Mute Indicator */}
        {isMuted && (
          <div style={styles.muteIndicator} role="status" aria-label={voiceStatus.mute.indicator}>
            <span style={styles.muteIcon} aria-hidden="true">🔇</span>
            <span style={styles.muteText}>{voiceStatus.mute.label}</span>
          </div>
        )}
      </div>

      {/* Session ID (if provided) */}
      {sessionId && (
        <div style={styles.sessionInfo}>
          <span style={styles.sessionLabel}>{voiceStatus.session.label}</span>
          <span style={styles.sessionId}>{sessionId.slice(0, 8)}...</span>
        </div>
      )}

      {/* Local Speaking Indicator */}
      {/* TODO: [4.1.2 Help Text] Add a first-time tooltip or contextual help explaining
          the VU meter (green bar = voice level) and how to troubleshoot if it doesn't move
          (e.g. "check that the correct microphone is selected in your browser"). */}
      <div style={styles.localSpeaker} role="status" aria-live="polite" aria-atomic="true" aria-label={localSpeaking && !isMuted ? voiceStatus.local.speaking : isMuted ? voiceStatus.local.muted : voiceStatus.local.notSpeaking}>
        <div
          style={{
            ...styles.speakerIcon,
            ...(localSpeaking && !isMuted ? styles.speakingActive : {}),
            animation: localSpeaking && !isMuted ? 'pulse 0.5s ease-in-out infinite' : 'none'
          }}
          aria-hidden="true"
        >
          {isMuted ? '🔇' : localSpeaking ? '🎤' : '🎙️'}
        </div>
        <div style={styles.vuMeter} role="progressbar" aria-label={formatMessage(voiceStatus.local.voiceLevel, { level: Math.round(localEnergy * 100) })} aria-valuenow={Math.round(localEnergy * 100)} aria-valuemin={0} aria-valuemax={100}>
          <div
            style={{
              ...styles.vuMeterFill,
              width: `${Math.min(100, localEnergy * 500)}%`,
              backgroundColor: localEnergy > 0.1 ? '#22c55e' : '#3b82f6'
            }}
            aria-hidden="true"
          />
        </div>
        <span style={styles.localLabel}>{voiceStatus.local.label}</span>
      </div>

      {/* Active Speakers */}
      {showParticipants && activeSpeakers.length > 0 && (
        <div style={styles.participantsList} role="list" aria-live="polite" aria-label={formatMessage(voiceStatus.participants.ariaLabel, { count: activeSpeakers.length, plural: activeSpeakers.length === 1 ? '' : 's' })}>
          <div style={styles.participantsHeader} role="heading" aria-level={3}>
            🔊 {formatMessage(voiceStatus.participants.header, { count: activeSpeakers.length })}
          </div>
          {activeSpeakers.map((participant) => {
            const { tooltip } = resolveDisplayName(
              participant.participantId,
              undefined,
              participant.characterName,
              participant.characterId,
              characterNameMap
            );
            return (
              <div key={participant.participantId} style={styles.participant} role="listitem" title={tooltip}>
                <span
                  style={{
                    ...styles.participantIcon,
                    animation: 'pulse 0.5s ease-in-out infinite'
                  }}
                  aria-hidden="true"
                >
                  🗣️
                </span>
                <span style={styles.participantName}>{participant.name}</span>
                <div style={styles.participantVuMeter} role="progressbar" aria-label={formatMessage(voiceStatus.participants.voiceLevel, { name: participant.name, level: Math.round(participant.energyLevel * 100) })} aria-valuenow={Math.round(participant.energyLevel * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    style={{
                      ...styles.vuMeterFill,
                      width: `${Math.min(100, participant.energyLevel * 500)}%`
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Component styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: '"Cinzel", serif',
    minWidth: '200px'
  },
  compactContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '20px',
    backdropFilter: 'blur(8px)'
  },
  compactIcon: {
    fontSize: '16px'
  },
  compactBadge: {
    fontSize: '11px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '10px',
    fontWeight: 'bold'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  statusIcon: {
    fontSize: '14px'
  },
  statusText: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)'
  },
  muteIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderRadius: '6px'
  },
  muteIcon: {
    fontSize: '12px'
  },
  muteText: {
    fontSize: '11px',
    color: '#fca5a5',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  sessionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)'
  },
  sessionLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  sessionId: {
    fontFamily: 'monospace',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  localSpeaker: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px'
  },
  speakerIcon: {
    fontSize: '20px',
    transition: 'transform 0.2s ease'
  },
  speakingActive: {
    transform: 'scale(1.1)'
  },
  vuMeter: {
    flex: 1,
    height: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden'
  },
  vuMeterFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: '2px',
    transition: 'width 0.1s ease'
  },
  localLabel: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    minWidth: '30px'
  },
  participantsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  participantsHeader: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px'
  },
  participant: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderRadius: '6px'
  },
  participantIcon: {
    fontSize: '14px'
  },
  participantName: {
    flex: 1,
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.8)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  participantVuMeter: {
    width: '40px',
    height: '3px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '1.5px',
    overflow: 'hidden'
  }
};

export default VoiceStatus;
